import {
  type InfiniteData,
  type Query,
  type UseInfiniteQueryOptions,
  type UseQueryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';

import * as Sentry from '@sentry/react';
import type { ImageType } from '@vizit/api';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import {
  type ApiRoutersPortfoliosAssetsImagesImagesImageViewFields,
  type SearchImagesV1WorkspacesWorkspaceIdAssetsImagesGetData,
  type SearchImagesV1WorkspacesWorkspaceIdAssetsImagesGetError,
  type SearchImagesV1WorkspacesWorkspaceIdAssetsImagesGetResponse,
  searchImagesV1WorkspacesWorkspaceIdAssetsImagesGet,
} from '../../generated';
import { extractTotalCount } from '../../utils';
import { PENDING_STALE_TIMEOUT_MS, getPendingImageTimestamps, pollWhilePending } from '../../utils/poll-while-pending';

export type SearchImagesQueryVariables = SearchImagesV1WorkspacesWorkspaceIdAssetsImagesGetData['path'] &
  SearchImagesV1WorkspacesWorkspaceIdAssetsImagesGetData['query'];

export type SearchImagesQuery = {
  data: SearchImagesV1WorkspacesWorkspaceIdAssetsImagesGetResponse;
  total_count: number;
};

export const useSearchImagesQuery = <
  TData = SearchImagesQuery,
  TError = SearchImagesV1WorkspacesWorkspaceIdAssetsImagesGetError,
>(
  {
    workspace_id,
    limit,
    offset,
    sort_by,
    sort_direction,
    parent_id,
    source,
    exclude_source,
    image_ids,
    product_category_id,
    image_type,
    tag_ids,
  }: SearchImagesQueryVariables,
  options?: Omit<UseQueryOptions<SearchImagesQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<SearchImagesQuery, TError, TData>({
    queryKey: [
      'SearchImages',
      {
        workspace_id,
        limit,
        offset,
        sort_by,
        sort_direction,
        parent_id,
        source,
        exclude_source,
        image_ids,
        product_category_id,
        image_type,
        tag_ids,
      },
    ],
    queryFn: async () => {
      const { data, response } = await searchImagesV1WorkspacesWorkspaceIdAssetsImagesGet({
        path: { workspace_id },
        query: {
          limit,
          offset,
          sort_by: sort_by as ApiRoutersPortfoliosAssetsImagesImagesImageViewFields | undefined,
          sort_direction,
          parent_id: parent_id as string[] | undefined,
          source: source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
          exclude_source: exclude_source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
          product_category_id: product_category_id as string[] | undefined,
          image_type: image_type as ImageType,
          ...(tag_ids?.length ? { tag_ids } : {}),
        },
        throwOnError: true,
      });
      return {
        data: data ?? [],
        total_count: extractTotalCount(response),
      };
    },
    ...options,
  });
};

useSearchImagesQuery.getKey = ({
  workspace_id,
  limit,
  offset,
  sort_by,
  sort_direction,
  parent_id,
  source,
  exclude_source,
  image_ids,
  product_category_id,
  image_type,
  tag_ids,
}: SearchImagesQueryVariables) => [
  'SearchImages',
  {
    workspace_id,
    limit,
    offset,
    sort_by,
    sort_direction,
    parent_id,
    source,
    exclude_source,
    image_ids,
    product_category_id,
    image_type,
    tag_ids,
  },
];

useSearchImagesQuery.fetcher = (
  {
    workspace_id,
    limit,
    offset,
    sort_by,
    sort_direction,
    parent_id,
    source,
    exclude_source,
    product_category_id,
    image_type,
    tag_ids,
  }: SearchImagesQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data, response } = await searchImagesV1WorkspacesWorkspaceIdAssetsImagesGet({
      path: { workspace_id },
      query: {
        limit,
        offset,
        sort_by: sort_by as ApiRoutersPortfoliosAssetsImagesImagesImageViewFields | undefined,
        sort_direction,
        parent_id: parent_id as string[] | undefined,
        source: source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
        exclude_source: exclude_source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
        product_category_id: product_category_id as string[] | null | undefined,
        image_type: image_type as ImageType,
        ...(tag_ids?.length ? { tag_ids } : {}),
      },
      throwOnError: true,
    });
    return {
      data: data ?? [],
      total_count: extractTotalCount(response),
    };
  };
};

export type SearchImagesInfiniteQueryVariables = Omit<SearchImagesQueryVariables, 'offset'>;

export const useSearchImagesInfiniteQuery = <
  TData = SearchImagesQuery,
  TError = SearchImagesV1WorkspacesWorkspaceIdAssetsImagesGetError,
>(
  {
    workspace_id,
    limit,
    sort_by,
    sort_direction,
    parent_id,
    source,
    exclude_source,
    image_ids,
    product_category_id,
    image_type,
    tag_ids,
  }: SearchImagesInfiniteQueryVariables,
  options?: Omit<
    UseInfiniteQueryOptions<SearchImagesQuery, TError, TData>,
    'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
  >,
) => {
  return useInfiniteQuery<SearchImagesQuery, TError, TData>({
    queryKey: [
      'SearchImagesInfinite',
      {
        workspace_id,
        limit,
        sort_by,
        sort_direction,
        parent_id,
        source,
        exclude_source,
        image_ids,
        product_category_id,
        image_type,
        tag_ids,
      },
    ],
    queryFn: async ({ pageParam = 0 }) => {
      const { data, response } = await searchImagesV1WorkspacesWorkspaceIdAssetsImagesGet({
        path: { workspace_id },
        query: {
          limit,
          offset: pageParam as number,
          sort_by: sort_by as ApiRoutersPortfoliosAssetsImagesImagesImageViewFields | undefined,
          sort_direction,
          parent_id: parent_id as string[] | undefined,
          source: source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
          exclude_source: exclude_source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
          image_ids: image_ids as string[] | undefined,
          product_category_id: product_category_id as string[] | undefined,
          image_type: image_type as ImageType,
          ...(tag_ids?.length ? { tag_ids } : {}),
        },
        throwOnError: true,
      });

      return {
        data: data ?? [],
        total_count: extractTotalCount(response),
      };
    },
    getNextPageParam: (lastPage, allPages) => {
      const pageSize = limit || 12; // Default to 12 if no limit specified
      const currentOffset = (allPages.length - 1) * pageSize;
      const nextOffset = currentOffset + pageSize;

      // Check if we have more data to fetch
      if (lastPage.data.length < pageSize || nextOffset >= lastPage.total_count) {
        return undefined; // No more pages
      }

      return nextOffset;
    },
    getPreviousPageParam: (_, allPages) => {
      // Return undefined when we're on the first/only page (initial load)
      // Return a value when we have multiple pages (subsequent loads)
      return allPages.length <= 1 ? undefined : 0;
    },
    initialPageParam: 0,
    refetchInterval: pollWhilePending(
      (query: Query<SearchImagesQuery, TError, InfiniteData<SearchImagesQuery, unknown>>) =>
        getPendingImageTimestamps((query.state.data?.pages ?? []).flatMap((page) => page.data)),
      {
        onTimeout: (pendingCount) => {
          Sentry.withScope((scope) => {
            scope.setFingerprint(['GET', '/v1/workspaces/{workspace_id}/assets/images', 'polling_timeout']);

            Sentry.captureException(new Error('Images still pending after stale window for search images query.'), {
              level: 'warning',
              tags: {
                feature: SENTRY_FEATURE_TAGS.Asset,
                stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Asset].assetPolling,
              },
              extra: {
                workspace_id,
                pending_count: pendingCount,
                stale_window_ms: PENDING_STALE_TIMEOUT_MS,
                query_variables: {
                  workspace_id,
                  limit,
                  sort_by,
                  sort_direction,
                  parent_id,
                  source,
                  exclude_source,
                  image_ids,
                  product_category_id,
                  image_type,
                  tag_ids,
                },
              },
            });
          });
        },
      },
    ),
    refetchIntervalInBackground: true,
    ...options,
  });
};

useSearchImagesInfiniteQuery.getKey = ({
  workspace_id,
  limit,
  sort_by,
  sort_direction,
  parent_id,
  source,
  exclude_source,
  image_ids,
  product_category_id,
  image_type,
  tag_ids,
}: SearchImagesInfiniteQueryVariables) => [
  'SearchImagesInfinite',
  {
    workspace_id,
    limit,
    sort_by,
    sort_direction,
    parent_id,
    source,
    exclude_source,
    image_ids,
    product_category_id,
    image_type,
    tag_ids,
  },
];

useSearchImagesInfiniteQuery.fetcher = (
  {
    workspace_id,
    limit,
    sort_by,
    sort_direction,
    parent_id,
    source,
    exclude_source,
    image_ids,
    product_category_id,
    image_type,
    tag_ids,
  }: SearchImagesInfiniteQueryVariables,
  offset = 0,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data, response } = await searchImagesV1WorkspacesWorkspaceIdAssetsImagesGet({
      path: { workspace_id },
      query: {
        limit,
        offset,
        sort_by: sort_by as ApiRoutersPortfoliosAssetsImagesImagesImageViewFields | undefined,
        sort_direction,
        parent_id: parent_id as string[] | undefined,
        source: source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
        exclude_source: exclude_source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
        image_ids: image_ids as string[] | undefined,
        product_category_id: product_category_id as string[] | undefined,
        image_type: image_type as ImageType,
        ...(tag_ids?.length ? { tag_ids } : {}),
      },
      throwOnError: true,
    });
    return {
      data: data ?? [],
      total_count: extractTotalCount(response),
    };
  };
};
